import { Criteria } from '@yandex-int/k-common/client/api/defs/get-criteria';
import {
    CRITERIA_MARK_VALUE,
    CriteriaMark,
    getCriteriaMarks,
} from '@yandex-int/k-common/client/api/defs/get-criteria-marks';
import { putCriteriaMarks } from '@yandex-int/k-common/client/api/defs/put-criteria-marks';
import { Radiobox, Text } from 'platform-components';
import { UI_KIT_TEXT_TYPOGRAPHY } from 'platform-components/constants';
import { CriteriaCircle } from 'platform-components/src/components/criteria-circle/criteria-circle';
import React, { ChangeEvent, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { queryCache, useMutation, useQuery } from 'react-query';

import './lesson-content__criteria.scss';

const options = [
    { value: String(CRITERIA_MARK_VALUE.YES), label: <FormattedMessage id="criteria.mark.yes" /> },
    { value: String(CRITERIA_MARK_VALUE.NO), label: <FormattedMessage id="criteria.mark.no" /> },
];

interface Props {
    problemId: number;
    attemptId: string | null;
    criteriaData: Criteria[];
}

export const LessonContentCriteria = ({ attemptId, problemId, criteriaData }: Props) => {
    const enabled = Boolean(attemptId);
    const criteriaMarks = useQuery(['getCriteriaMarks', attemptId], () => getCriteriaMarks(attemptId!), {
        enabled,
    });
    const marks = useMemo(
        () => criteriaMarks.data?.filter((m) => criteriaData.some((c) => c.id === m.criterion_id)),
        [criteriaData, criteriaMarks.data]
    );

    const [updateCriteria] = useMutation(
        async (mark: CriteriaMark) => {
            if (attemptId) {
                return putCriteriaMarks({
                    ...mark,
                    attempt_id: attemptId,
                    object_id: String(problemId),
                });
            }
            return undefined;
        },
        {
            onMutate: (mark) => {
                const key = ['getCriteriaMarks', attemptId];
                queryCache.cancelQueries(key);
                const previousMarks = queryCache.getQueryData(key);
                queryCache.setQueryData(key, (marksData: CriteriaMark[] | undefined) => {
                    marksData = marksData?.filter((m) => m.criterion_id !== mark.criterion_id) || [];
                    marksData.push(mark);
                    return marksData;
                });
                return previousMarks;
            },
            onError: (_err, _mark, previousMarks) =>
                queryCache.setQueryData(['getCriteriaMarks', attemptId], previousMarks),
            onSettled: () => queryCache.invalidateQueries(['getCriteriaMarks', attemptId]),
        }
    );

    return (
        <div className="lesson-content__criteria">
            <div className="lesson-content__criteria-header-wrapper">
                <div className="lesson-content__criteria-header">
                    <div className="lesson-content__criteria-header-title">
                        <Text id="criteria.rateStudent" typography={UI_KIT_TEXT_TYPOGRAPHY.paragraphXLMedium} />
                    </div>
                    <CriteriaCircle
                        correct={marks?.filter((m) => m.value === CRITERIA_MARK_VALUE.YES).length || 0}
                        incorrect={marks?.filter((m) => m.value === CRITERIA_MARK_VALUE.NO).length || 0}
                        max={criteriaData.length}
                    />
                </div>
            </div>
            <div className="lesson-content__criteria-list-wrapper">
                <ol className="lesson-content__criteria-list">
                    {criteriaData.map((criterion) => {
                        const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
                            updateCriteria({
                                criterion_id: criterion.id,
                                value: parseInt(e.target.value, 10) as CRITERIA_MARK_VALUE,
                            });
                        };
                        return (
                            <li className="lesson-content__criteria-list-item" key={criterion.id}>
                                <Text typography={UI_KIT_TEXT_TYPOGRAPHY.paragraphLRegular}>{criterion.name}</Text>
                                <div className="lesson-content__criteria-radiobox">
                                    <Radiobox
                                        disabled={!enabled}
                                        onChange={enabled ? handleChange : undefined}
                                        options={options}
                                        size="m"
                                        value={marks?.find((m) => m.criterion_id === criterion.id)?.value.toString()}
                                        view="outline"
                                    />
                                </div>
                            </li>
                        );
                    })}
                </ol>
            </div>
        </div>
    );
};
